<template>
  <div class="bg-light border-top">

    <!-- ── Header ─────────────────────────────────────────────── -->
    <div class="bg-white border-bottom px-3 py-2 d-flex justify-content-between align-items-center">
      <h6 class="mb-0 text-dark font-weight-bold">
        <i class="fas fa-chart-line text-primary mr-2"></i>KDS Admin Panel
      </h6>
      <button class="btn btn-sm btn-outline-secondary" @click="refresh" :disabled="loading">
        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
        Refresh
      </button>
    </div>

    <div class="container-fluid py-3 px-3">

      <!-- ── Stat cards ───────────────────────────────────────── -->
      <div class="row mb-4" v-if="stats">
        <div class="col-6 col-md-4 col-xl-2 mb-2" v-for="card in statCards" :key="card.label">
          <div class="card border shadow-sm h-100 text-center">
            <div class="card-body p-2">
              <div class="font-weight-bold text-dark mb-1" style="font-size:1.5rem;">{{ card.value }}</div>
              <div class="text-muted" style="font-size:.68rem;text-transform:uppercase;letter-spacing:.7px;">{{ card.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">

        <!-- ── Station management ─────────────────────────────── -->
        <div class="col-12 col-lg-5 mb-4">
          <div class="card border shadow-sm">
            <div class="card-header bg-white py-2 d-flex justify-content-between align-items-center">
              <span class="text-dark font-weight-bold" style="font-size:.8rem;text-transform:uppercase;letter-spacing:.7px;">
                <i class="fas fa-server text-primary mr-2"></i>Stations
              </span>
              <button class="btn btn-xs btn-outline-secondary" @click="loadStations">
                <i class="fas fa-sync-alt"></i>
              </button>
            </div>
            <div v-if="stationsLoading" class="text-center py-3">
              <span class="spinner-border spinner-border-sm text-secondary"></span>
            </div>
            <div v-else-if="!stations.length" class="text-center text-muted py-3">
              No stations configured.
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>Station</th>
                    <th class="text-center">SLA (min)</th>
                    <th class="text-center">Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="station in stations" :key="station.id">
                    <td class="align-middle">
                      <span class="font-weight-bold text-dark">{{ station.display_name || station.name }}</span>
                      <span v-if="station.is_fallback" class="badge badge-secondary ml-1" style="font-size:.58rem;">fallback</span>
                    </td>
                    <td class="text-center align-middle">
                      <span
                        v-if="!editingSla[station.id]"
                        class="text-primary font-weight-bold kds-editable"
                        @click="startSlaEdit(station)"
                        title="Click to edit"
                      >{{ station.sla_minutes || '—' }}</span>
                      <div v-else class="d-flex align-items-center justify-content-center">
                        <input
                          type="number"
                          class="form-control form-control-sm kds-sla-input"
                          v-model.number="slaEdits[station.id]"
                          min="1" max="120"
                          @keyup.enter="saveSla(station)"
                          @keyup.escape="cancelSlaEdit(station)"
                        >
                        <button class="btn btn-xs btn-primary ml-1" @click="saveSla(station)">
                          <i class="fas fa-check"></i>
                        </button>
                      </div>
                    </td>
                    <td class="text-center align-middle">
                      <span class="badge" :class="station.is_paused ? 'badge-secondary' : 'badge-primary'">
                        {{ station.is_paused ? 'Paused' : 'Active' }}
                      </span>
                    </td>
                    <td class="text-right align-middle">
                      <button
                        class="btn btn-xs"
                        :class="station.is_paused ? 'btn-outline-primary' : 'btn-outline-secondary'"
                        @click="toggleStation(station)"
                        :disabled="!!togglingStation[station.id]"
                      >
                        <span v-if="togglingStation[station.id]" class="spinner-border spinner-border-sm" style="width:.6rem;height:.6rem;"></span>
                        <span v-else>{{ station.is_paused ? 'Resume' : 'Pause' }}</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- ── Per-station stats ───────────────────────────────── -->
        <div class="col-12 col-lg-7 mb-4" v-if="stats && stats.by_station && stats.by_station.length">
          <div class="card border shadow-sm">
            <div class="card-header bg-white py-2">
              <span class="text-dark font-weight-bold" style="font-size:.8rem;text-transform:uppercase;letter-spacing:.7px;">
                <i class="fas fa-table text-primary mr-2"></i>Today by Station
              </span>
            </div>
            <div class="table-responsive">
              <table class="table table-sm table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>Station</th>
                    <th class="text-center">Orders</th>
                    <th class="text-center">Completed</th>
                    <th class="text-center">SLA Breaches</th>
                    <th class="text-center">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in stats.by_station" :key="row.prep_station">
                    <td class="font-weight-bold text-dark">{{ capitalize(row.prep_station || 'General') }}</td>
                    <td class="text-center">{{ row.total }}</td>
                    <td class="text-center text-primary font-weight-bold">{{ row.completed }}</td>
                    <td class="text-center" :class="row.breaches > 0 ? 'text-dark font-weight-bold' : 'text-muted'">
                      {{ row.breaches }}
                    </td>
                    <td class="text-center">
                      <span class="badge" :class="stationRateBadge(row)">
                        {{ row.total > 0 ? Math.round((row.completed / row.total) * 100) : 0 }}%
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>

      <!-- ── Admin actions ──────────────────────────────────────── -->
      <div class="row mb-4">
        <div class="col-12 col-md-6">
          <div class="card border shadow-sm">
            <div class="card-header bg-white py-2">
              <span class="text-dark font-weight-bold" style="font-size:.8rem;text-transform:uppercase;letter-spacing:.7px;">
                <i class="fas fa-bolt text-dark mr-2"></i>Force Complete
              </span>
            </div>
            <div class="card-body">
              <p class="text-muted small mb-2">Admin override: mark any KDS order as done regardless of its current state.</p>
              <div class="input-group input-group-sm">
                <input
                  type="text"
                  class="form-control border-secondary"
                  placeholder="KDS Order ID (UUID)"
                  v-model="forceOrderId"
                >
                <div class="input-group-append">
                  <button
                    class="btn btn-dark"
                    @click="forceComplete"
                    :disabled="!forceOrderId.trim() || forceLoading"
                  >
                    <span v-if="forceLoading" class="spinner-border spinner-border-sm mr-1"></span>
                    Force Complete
                  </button>
                </div>
              </div>
              <div v-if="forceMsg" class="mt-2" :class="forceMsgClass">{{ forceMsg }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Activity logs ──────────────────────────────────────── -->
      <div class="card border shadow-sm mb-4">
        <div class="card-header bg-white py-2 d-flex justify-content-between align-items-center">
          <span class="text-dark font-weight-bold" style="font-size:.8rem;text-transform:uppercase;letter-spacing:.7px;">
            <i class="fas fa-history text-primary mr-2"></i>Recent Activity
          </span>
          <button class="btn btn-xs btn-outline-secondary" @click="loadLogs()">Reload</button>
        </div>

        <div v-if="logsLoading && !logs.length" class="text-center py-3">
          <span class="spinner-border spinner-border-sm text-secondary"></span>
        </div>
        <div v-else-if="!logs.length" class="text-center text-muted py-3">No logs found.</div>
        <div v-else class="kds-log-list">
          <div
            v-for="log in logs"
            :key="log.id"
            class="d-flex align-items-start px-3 py-2 border-bottom bg-white kds-log-row"
          >
            <!-- Icon -->
            <div class="rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0 kds-log-icon" :class="logIconClass(log.action)">
              <i :class="logIcon(log.action)" style="font-size:.7rem;"></i>
            </div>
            <!-- Content -->
            <div class="flex-grow-1" style="min-width:0;">
              <div>
                <span class="font-weight-bold text-dark" style="font-size:.82rem;">{{ formatAction(log.action) }}</span>
                <span class="text-muted ml-2">{{ log.message }}</span>
              </div>
              <div class="text-muted mt-1" style="font-size:.7rem;">
                <span v-if="log.user" class="mr-2"><i class="fas fa-user mr-1"></i>{{ log.user.name }}</span>
                <span v-if="log.station" class="mr-2"><i class="fas fa-map-marker-alt mr-1"></i>{{ log.station }}</span>
                <span><i class="fas fa-clock mr-1"></i>{{ fmtDatetime(log.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Load more -->
        <div v-if="logsNextPage" class="text-center py-2 bg-white border-top">
          <button class="btn btn-sm btn-outline-secondary" @click="loadMoreLogs" :disabled="logsLoading">
            Load more
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'KdsAdminDashboard',

  data() {
    return {
      stats:           null,
      stations:        [],
      logs:            [],
      loading:         false,
      stationsLoading: false,
      logsLoading:     false,
      logsPage:        1,
      logsNextPage:    false,
      togglingStation: {},
      editingSla:      {},
      slaEdits:        {},
      forceOrderId:    '',
      forceLoading:    false,
      forceMsg:        '',
      forceMsgClass:   'text-primary',
    };
  },

  computed: {
    statCards() {
      if (!this.stats) return [];
      return [
        { label: 'Orders Today',    value: this.stats.total_today },
        { label: 'Completed',       value: this.stats.completed_today },
        { label: 'Completion Rate', value: this.stats.completion_rate + '%' },
        { label: 'Active Now',      value: this.stats.pending_now },
        { label: 'SLA Breaches',    value: this.stats.sla_breaches_today },
        { label: 'Avg Prep (min)',  value: this.stats.avg_completion_minutes ?? '—' },
      ];
    },
  },

  mounted() {
    this.refresh();
  },

  methods: {
    async refresh() {
      this.loading = true;
      await Promise.all([this.loadStats(), this.loadStations(), this.loadLogs()]);
      this.loading = false;
    },

    async loadStats() {
      try {
        const { data } = await axios.get('/api/kds/admin/stats');
        this.stats = data;
      } catch (err) {
        console.error('[KDS Admin] stats error:', err);
      }
    },

    async loadStations() {
      this.stationsLoading = true;
      try {
        const { data } = await axios.get('/api/kds/admin/stations');
        this.stations = data.data || [];
      } catch (err) {
        console.error('[KDS Admin] stations error:', err);
      } finally {
        this.stationsLoading = false;
      }
    },

    async loadLogs(page = 1) {
      this.logsLoading = true;
      try {
        const { data } = await axios.get('/api/kds/admin/logs', { params: { per_page: 30, page } });
        const incoming  = data.data || [];
        this.logs       = page === 1 ? incoming : [...this.logs, ...incoming];
        this.logsPage      = data.current_page;
        this.logsNextPage  = data.current_page < data.last_page;
      } catch (err) {
        console.error('[KDS Admin] logs error:', err);
      } finally {
        this.logsLoading = false;
      }
    },

    loadMoreLogs() {
      this.loadLogs(this.logsPage + 1);
    },

    async toggleStation(station) {
      this.$set(this.togglingStation, station.id, true);
      try {
        const { data } = await axios.post(`/api/kds/admin/stations/${station.id}/toggle`, {
          is_paused: !station.is_paused,
        });
        const idx = this.stations.findIndex(s => s.id === station.id);
        if (idx !== -1) this.$set(this.stations, idx, data.data);
      } catch (err) {
        console.error('[KDS Admin] toggle error:', err);
      } finally {
        this.$delete(this.togglingStation, station.id);
      }
    },

    startSlaEdit(station) {
      this.$set(this.slaEdits,   station.id, station.sla_minutes || 10);
      this.$set(this.editingSla, station.id, true);
    },
    cancelSlaEdit(station) {
      this.$delete(this.editingSla, station.id);
    },
    async saveSla(station) {
      const mins = this.slaEdits[station.id];
      if (!mins || mins < 1) return;
      try {
        const { data } = await axios.put(`/api/kds/admin/stations/${station.id}/sla`, { sla_minutes: mins });
        const idx = this.stations.findIndex(s => s.id === station.id);
        if (idx !== -1) this.$set(this.stations, idx, data.data);
        this.$delete(this.editingSla, station.id);
      } catch (err) {
        console.error('[KDS Admin] saveSla error:', err);
      }
    },

    async forceComplete() {
      if (!this.forceOrderId.trim()) return;
      this.forceLoading = true;
      this.forceMsg     = '';
      try {
        await axios.post('/api/kds/admin/force-complete', { kds_order_id: this.forceOrderId.trim() });
        this.forceMsg      = 'Order force-completed successfully.';
        this.forceMsgClass = 'text-primary';
        this.forceOrderId  = '';
      } catch (err) {
        this.forceMsg      = err.response?.data?.message || 'Failed to force complete.';
        this.forceMsgClass = 'text-dark';
      } finally {
        this.forceLoading = false;
      }
    },

    // ── Helpers ────────────────────────────────────────────────

    capitalize(str) {
      return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
    },

    fmtDatetime(dt) {
      if (!dt) return '';
      return new Date(dt).toLocaleString('en-KE', {
        day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit',
      });
    },

    stationRateBadge(row) {
      const rate = row.total > 0 ? (row.completed / row.total) * 100 : 0;
      if (rate >= 80) return 'badge-primary';
      if (rate >= 50) return 'badge-secondary';
      return 'badge-dark';
    },

    formatAction(action) {
      return (action || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },

    logIcon(action) {
      const map = {
        acknowledged:    'fas fa-bell',
        completed:       'fas fa-check-circle',
        cancelled:       'fas fa-times-circle',
        force_completed: 'fas fa-bolt',
        item_added:      'fas fa-plus-circle',
        item_merged:     'fas fa-code-branch',
        kds_sent:        'fas fa-paper-plane',
        print_enqueued:  'fas fa-print',
        sla_breached:    'fas fa-exclamation-triangle',
      };
      return map[action] || 'fas fa-circle';
    },

    logIconClass(action) {
      if (['cancelled', 'sla_breached'].includes(action))       return 'kds-log-icon--dark';
      if (['completed', 'force_completed'].includes(action))     return 'kds-log-icon--primary';
      if (['acknowledged', 'item_added', 'kds_sent'].includes(action)) return 'kds-log-icon--secondary';
      return 'kds-log-icon--default';
    },
  },
};
</script>

<style scoped>
/* Stat card min height */
.card-body { min-height: 70px; display: flex; flex-direction: column; justify-content: center; }

/* Station SLA inline input */
.kds-sla-input {
  width: 58px;
  padding: .15rem .4rem;
  font-size: .8rem;
}

/* Editable SLA value */
.kds-editable {
  cursor: pointer;
  border-bottom: 1px dashed;
}
.kds-editable:hover { opacity: .7; }

/* Log list */
.kds-log-list { max-height: 420px; overflow-y: auto; }
.kds-log-row { transition: background .15s; }
.kds-log-row:hover { background: #f8f9fa !important; }
.kds-log-row:last-child { border-bottom: none !important; }

/* Log icon circles */
.kds-log-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  margin-top: 1px;
}
.kds-log-icon--primary   { background: #cce5ff; color: #004085; }
.kds-log-icon--secondary { background: #e2e3e5; color: #383d41; }
.kds-log-icon--dark      { background: #d6d8d9; color: #1b1e21; }
.kds-log-icon--default   { background: #f8f9fa; color: #6c757d; }

/* Tiny button */
.btn-xs {
  padding: .1rem .36rem;
  font-size: .68rem;
  border-radius: 3px;
  line-height: 1.4;
}
</style>
